/**
 * @file BackupSourcesFields.tsx
 * @description «Что копировать» (phase19.26): база данных PostgreSQL и бакеты
 * хранилища файлов из реестра `BACKUP_BUCKETS`.
 *
 * Каждая строка — тач-цель ≥ 48px; имя бакета моноширинным под русской
 * подписью, чтобы сверять с хранилищем.
 */

import { Checkbox } from "../../ui/checkbox";
import { SectionLabel } from "../../ui/SectionLabel";
import { BACKUP_BUCKETS } from "../../../lib/api-backups";

interface Props {
  databaseIncluded: boolean;
  bucketsIncluded: string[];
  onChange: (patch: { databaseIncluded?: boolean; bucketsIncluded?: string[] }) => void;
  disabled?: boolean;
}

export function BackupSourcesFields({
  databaseIncluded,
  bucketsIncluded,
  onChange,
  disabled,
}: Props) {
  function toggleBucket(id: string, on: boolean) {
    const next = on
      ? [...new Set([...bucketsIncluded, id])]
      : bucketsIncluded.filter((b) => b !== id);
    onChange({ bucketsIncluded: next });
  }

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>Что копировать</SectionLabel>
      <SourceRow
        id="bk-db"
        label="База данных PostgreSQL"
        hint="Дамп всей базы: заказы, клиенты, каталог, склад"
        checked={databaseIncluded}
        onChange={(v) => onChange({ databaseIncluded: v })}
        disabled={disabled}
      />
      {BACKUP_BUCKETS.map((b) => (
        <SourceRow
          key={b.id}
          id={`bk-${b.id}`}
          label={b.label}
          hint={b.id}
          mono
          checked={bucketsIncluded.includes(b.id)}
          onChange={(v) => toggleBucket(b.id, v)}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function SourceRow({
  id,
  label,
  hint,
  mono = false,
  checked,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint: string;
  mono?: boolean;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl bg-surface-low px-3 py-2"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(Boolean(v))}
        disabled={disabled}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm text-on-surface">{label}</span>
        <span
          className={
            mono
              ? "truncate font-mono text-xs text-on-surface-variant"
              : "text-xs text-on-surface-variant"
          }
        >
          {hint}
        </span>
      </span>
    </label>
  );
}
